import cloneDeep from 'lodash.clonedeep';
import { formatFormDate } from '@general/js/utils';
import { HospitalSituation } from '@/enums';
import { isEmpty } from 'lodash';
import { LifetimeHealthCoverSituation } from '@general/js/enums';

export const mapVeteranCard = (personData, veteranCard, noLongerEligibleForVeteran) => {
  const personObj = cloneDeep(personData);
  // In mapPerson function, those fields are under personObj not personObj.veteranCard;
  personObj.haveVeteranCard = noLongerEligibleForVeteran ? false : veteranCard.haveVeteranCard;
  personObj.veteranCardType = noLongerEligibleForVeteran ? '' : veteranCard.veteranCardType;
  personObj.veteranCardConditionOptions = noLongerEligibleForVeteran ? [] : veteranCard.veteranCardConditionOptions;

  return personObj;
};

export const productsABDEligible = (allProducts) => {
  const products = allProducts.map((obj) => {
    return obj.eligibleForABD;
  });

  return products.some(Boolean) || false;
};

export const mapLhcPayload = (lhcDetails) => {
  const lifetimeHealthCoverOverrule = {};

  if (lhcDetails.overruledReason?.toLowerCase() === LifetimeHealthCoverSituation.NONE.toLowerCase()) {
    return null;
  }

  if (![null, undefined, ''].includes(lhcDetails?.cae)) {
    lifetimeHealthCoverOverrule.cae = Number(lhcDetails.cae);
  }

  if (lhcDetails.overruledReason) {
    lifetimeHealthCoverOverrule.overruledReason = lhcDetails.overruledReason;
  }

  if (lhcDetails.date) {
    lifetimeHealthCoverOverrule.date = formatFormDate(lhcDetails.date);
  }
  if (lhcDetails.dischargeDate) {
    lifetimeHealthCoverOverrule.dischargeDate = formatFormDate(lhcDetails.dischargeDate);
  }

  return isEmpty(lifetimeHealthCoverOverrule) ? null : lifetimeHealthCoverOverrule;
};

/**
 * This function is used to format quote LHC and ABD records depending on the hospital state of the person
 * @param {*} personDetails Details of person
 * @param {*} hospitalState State of hospital cover, either APPLYING, REAPPLYING, or RETAINING
 * @param {*} existingLhc  Boolean to check if use has an existing LHC record
 * @returns
 */
export const formatQuoteLHCABD = (personDetails, hospitalState, existingLhcOverrule = false) => {
  if (hospitalState === HospitalSituation.APPLYING) {
    // Send all information captured in form
    return {
      abd: !isEmpty(personDetails.abd)
        ? {
            firstTimePHI: personDetails.abd.isFirstTime,
            currentlyReceived: personDetails.abd.currentlyReceivingABD,
            cda: personDetails.abd.cda,
          }
        : null,
      lhc: !existingLhcOverrule && !isEmpty(personDetails.lhc) ? mapLhcPayload(personDetails.lhc) : null,
    };
  }
  if (hospitalState === HospitalSituation.REAPPLYING) {
    // For no existing LHC record (overrule reason) we send the lhc object if not empty
    const returnObj = {
      abd: {
        cda: personDetails.abd?.cda,
      },
      lhc: !existingLhcOverrule && !isEmpty(personDetails.lhc) ? mapLhcPayload(personDetails.lhc) : null,
    };
    return returnObj;
  }
  if (hospitalState === HospitalSituation.RETAINING) {
    // No updates required
    return {
      abd: null,
      lhc: null,
    };
  }
  return null;
};
/**
 * This function is used to format quote LHC and ABD records depending on the hospital state of the person when coming from email quote
 * @param {*} emailQuoteDetails Details of person from email quote
 * @param {*} hospitalState State of hospital cover, either APPLYING, REAPPLYING, or RETAINING
 * @param {*} existingLhcRecord Whether person has an existing LHC overrule record
 * @returns Formatted LHC and ABD object
 */
export const formatEmailQuoteLHCABD = (emailQuoteDetails, hospitalState, existingLhcOverrule = false) => {
  if (hospitalState === HospitalSituation.APPLYING) {
    // Send all information captured in form unless there is an existing lhcOverrule then send null for lhc
    return {
      abd: !isEmpty(emailQuoteDetails.ageBasedDiscount)
        ? {
            firstTimePHI: emailQuoteDetails.ageBasedDiscount.firstTimePHI,
            currentlyReceived: emailQuoteDetails.ageBasedDiscount.currentlyReceived,
            cda: emailQuoteDetails.ageBasedDiscount.cda,
          }
        : null,
      lhc: !existingLhcOverrule && !isEmpty(emailQuoteDetails.lifetimeHealthCoverOverrule) ? mapLhcPayload(emailQuoteDetails.lifetimeHealthCoverOverrule) : null,
    };
  }
  if (hospitalState === HospitalSituation.REAPPLYING) {
    // For existing LHC record (overrule reason) return ABD with cda and LHC as null as we do not capture total absent days in MP
    const returnObj = {
      abd: {
        cda: emailQuoteDetails.ageBasedDiscount?.cda,
      },
      lhc: !existingLhcOverrule && !isEmpty(emailQuoteDetails.lifetimeHealthCoverOverrule) ? mapLhcPayload(emailQuoteDetails.lifetimeHealthCoverOverrule) : null,
    };
    return returnObj;
  }
  if (hospitalState === HospitalSituation.RETAINING) {
    // No updates required
    return {
      abd: null,
      lhc: null,
    };
  }
  return null;
};

export const formatEaiLhcToObj = (lhcDetails) => {
  const lhc = {
    lhcLoadingRate: lhcDetails?.lhcLoadingRate,
    overruledReason: lhcDetails?.code,
    startDate: lhcDetails?.lhcStartDate,
    cae: lhcDetails?.certifiedAgeOfEntry,
  };

  if (lhcDetails?.medicareRegistrationDate) {
    lhc.date = lhcDetails.medicareRegistrationDate;
  }

  if (lhcDetails?.returnFromOverseasDate) {
    lhc.date = lhcDetails.returnFromOverseasDate;
  }

  return lhc;
};
